import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Checkbox, Divider, FormControlLabel, IconButton, Modal, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { immersightBlue } from '../../utils/colors';
import { PLATFORM_URL } from '../../utils/constants';

const useStyles = makeStyles((theme) => ({
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: 900,
    height: '90vh',
    maxHeight: 600,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 15,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  modalTitle: {
    fontSize: '1.3rem !important',
    fontWeight: 500,
    color: '#464646',
  },
  controlBar: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  gridContainer: {
    flex: 1,
    overflowY: 'auto',
    marginTop: theme.spacing(1), 
    paddingRight: theme.spacing(1),
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', 
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr', 
      gap: theme.spacing(2),
    },
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    transition: '0.2s',
    border: '2px solid transparent',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  cardSelected: {
    border: `2px solid ${immersightBlue}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    backgroundColor: 'rgba(0, 159, 227, 0.04)',
  },
  thumbContainer: {
    width: '100%',
    height: 160,
    minHeight: 160,
    maxHeight: 160,
    flexShrink: 0,
    borderRadius: 10,
    marginBottom: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  placeholderThumb: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#ccc',
  },
  filename: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.95rem !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    display: 'block',
  },
  divider: {
    marginLeft: '-24px !important',
    marginRight: '-24px !important',
    backgroundColor: '#d3d3d3',
    height: '1px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  actionBtn: {
    backgroundColor: `${immersightBlue} !important`,
    color: '#fff !important',
    padding: theme.spacing(1, 3),
    fontSize: '0.9rem !important',
    borderRadius: 20,
    textTransform: 'uppercase',
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
      color: 'rgba(0, 0, 0, 0.26) !important',
    },
  },
  checkboxOverlay: {
    position: 'absolute',
    top: 8,  
    right: 8,
    zIndex: 2,
    backgroundColor: '#fff',
    padding: 0, 
    borderRadius: 4, 
    display: 'flex', 
    alignItems: 'center',
    justifyContent: 'center',
    width: 24, 
    height: 24, 
  },
}));

interface PanoMeasurementSelectionModalProps {
  open: boolean;
  measurements: any[];
  onImport: (measurements: any[]) => void;
  onClose: () => void;
}

const PanoramaMeasurementSelectionModal: React.FC<PanoMeasurementSelectionModalProps> = ({ 
  open, 
  measurements, 
  onImport, 
  onClose 
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedIndices(new Set());
    }
  }, [open]);

  const getImageUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const cleanOrigin = PLATFORM_URL.endsWith('/') ? PLATFORM_URL.slice(0, -1) : PLATFORM_URL;
    return `${cleanOrigin}${cleanPath}`;
  };

  const toggleSelection = (index: number) => {
    const newSet = new Set(selectedIndices);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedIndices(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIndices.size === measurements.length) {
      setSelectedIndices(new Set());
    } else {
      const allIndices = new Set(measurements.map((_, i) => i));
      setSelectedIndices(allIndices);
    }
  };

  const handleImport = () => {
    const selectedItems = measurements.filter((_, index) => selectedIndices.has(index));
    onImport(selectedItems);
  };

  const allSelected = measurements.length > 0 && selectedIndices.size === measurements.length;
  const indeterminate = selectedIndices.size > 0 && selectedIndices.size < measurements.length;

  const renderCard = (measurement: any, index: number) => {
    const isSelected = selectedIndices.has(index);
    const hasDescription = measurement.description?.trim().length > 0;
    const hasTitlePicture = measurement.titlePicture && measurement.titlePicture !== '';

    const titleText = hasDescription 
      ? measurement.description 
      : `${intl.formatMessage({ id: 'General.measurement', defaultMessage: 'Vermessung' })} ${index + 1}`;

    return (
      <Box 
        key={index} 
        className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`} 
        onClick={() => toggleSelection(index)}
      >
        <Box className={classes.checkboxOverlay}>
          <Checkbox 
            checked={isSelected}
            size="small"
            style={{ 
              color: "#464646", 
              padding: 4 
            }}
            
          />
        </Box>

        <div className={classes.thumbContainer}>
          {hasTitlePicture ? (
            <img 
              src={getImageUrl(measurement.titlePicture)} 
              alt={titleText} 
              className={classes.thumb}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <Typography className={classes.placeholderThumb}>
              {index + 1}
            </Typography>
          )}
        </div>

        <Typography className={classes.filename} title={titleText}>
          {titleText}
        </Typography>
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.5)' } }}>
      <Box className={classes.modalBox}>
        <div className={classes.modalHeader}>
          <Typography className={classes.modalTitle}>
            {intl.formatMessage({ id: 'PanoramaMeasurements.choose', defaultMessage: 'Vermessung wählen' })}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon style={{ color: '#464646' }} />
          </IconButton>
        </div>

        <Divider className={classes.divider} />

        <div className={classes.controlBar}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={allSelected} 
                indeterminate={indeterminate}
                onChange={toggleSelectAll}
                size="small"
                style={{ color: "#464646" }}
              />
            }
            label={
              <Typography style={{ color: '#333', fontSize: '0.9rem', fontWeight: 500 }}>
                {intl.formatMessage({ id: 'General.selectAll', defaultMessage: 'alle auswählen' })}
              </Typography>
            }
            style={{ marginLeft: -8 }} 
          />
        </div>

        {/* Scrollable Grid */}
        <div className={classes.gridContainer}>
          {measurements.length === 0 ? (
            <Typography style={{ color: '#666', fontSize: '0.9rem' }}>
              {intl.formatMessage({ id: 'PanoramaSelect.empty', defaultMessage: 'Keine Panoramen gefunden.' })}
            </Typography>
          ) : (
            <div className={classes.grid}>
              {measurements.map((m, i) => renderCard(m, i))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={classes.footer}>

          <Button 
            disabled={selectedIndices.size === 0} 
            className={classes.actionBtn} 
            onClick={handleImport}
          >
            {intl.formatMessage(
              { id: 'PanoramaMeasurements.importCount', defaultMessage: 'IMPORTIEREN ({count})' },
              { count: selectedIndices.size }
            )}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default PanoramaMeasurementSelectionModal;